import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { PasswordInput } from '../components/ui/password-input';
import { Check, Zap, Crown, Building2, Users, Sparkles } from 'lucide-react';
import { maskCNPJ, cleanCNPJ, validateCNPJ } from '../utils/brazilValidation';

const API = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = select plan, 2 = registration form
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [cnpjError, setCnpjError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome: '',
    company: {
      nome: '',
      cnpj: '',
      telefone: '',
      numero_funcionarios: '',
      setor_atuacao: '',
      selected_plan: 'starter'
    }
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/api/asaas/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setFormData(prev => ({
      ...prev,
      company: { ...prev.company, selected_plan: planId }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate terms acceptance
    if (!termsAccepted) {
      toast.error('Você precisa aceitar os Termos de Uso e a Política de Privacidade');
      return;
    }
    
    // Validate CNPJ
    if (!validateCNPJ(formData.company.cnpj)) {
      toast.error('CNPJ inválido');
      return;
    }
    
    setLoading(true);

    try {
      const data = {
        ...formData,
        company: {
          ...formData.company,
          cnpj: cleanCNPJ(formData.company.cnpj),
          numero_funcionarios: parseInt(formData.company.numero_funcionarios) || 0,
          selected_plan: selectedPlan
        },
        terms_accepted: true,
        terms_version: '1.0'
      };
      await register(data);
      toast.success('Cadastro realizado com sucesso! Você tem 7 dias de teste grátis.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('company.')) {
      const field = name.split('.')[1];
      
      // Handle CNPJ masking
      if (field === 'cnpj') {
        const masked = maskCNPJ(value);
        setFormData(prev => ({
          ...prev,
          company: { ...prev.company, cnpj: masked }
        }));
        
        const cleaned = cleanCNPJ(value);
        if (cleaned.length === 14) {
          if (!validateCNPJ(cleaned)) {
            setCnpjError('CNPJ inválido');
          } else {
            setCnpjError('');
          }
        } else {
          setCnpjError('');
        }
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        company: { ...prev.company, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Zap className="h-6 w-6" />;
      case 'professional': return <Crown className="h-6 w-6" />;
      case 'enterprise': return <Building2 className="h-6 w-6" />;
      case 'master': return <Sparkles className="h-6 w-6" />;
      default: return <Zap className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'starter': return 'from-emerald-500 to-emerald-600';
      case 'professional': return 'from-[#0d9488] to-[#1e3a5f]';
      case 'enterprise': return 'from-purple-500 to-purple-600';
      case 'master': return 'from-amber-500 to-amber-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#e8f4fc] dark:bg-slate-950 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl md:text-4xl font-extrabold text-[#1e3a5f] dark:text-white mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Comece agora com CERP Brasil NR1
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {step === 1 ? 'Escolha o plano ideal para sua empresa' : 'Complete seu cadastro para iniciar o teste grátis'}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#0d9488]' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#0d9488] text-white' : 'bg-gray-200 text-gray-500'}`}>
              1
            </div>
            <span className="font-medium hidden sm:inline">Escolher Plano</span>
          </div>
          <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-[#0d9488]' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#0d9488]' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#0d9488] text-white' : 'bg-gray-200 text-gray-500'}`}>
              2
            </div>
            <span className="font-medium hidden sm:inline">Cadastro</span>
          </div>
        </div>

        {/* Step 1: Plan Selection */}
        {step === 1 && (
          <div data-testid="plan-selection-step">
            {/* Trial Banner */}
            <div className="bg-gradient-to-r from-[#0d9488] to-[#1e3a5f] rounded-2xl p-4 mb-8 text-center text-white">
              <p className="text-lg font-semibold">
                <Sparkles className="inline h-5 w-5 mr-2" />
                7 dias de teste GRÁTIS em todos os planos!
              </p>
              <p className="text-sm opacity-90">Sem necessidade de cartão de crédito. Cancele quando quiser.</p>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {plans.map((plan) => (
                <Card
                  key={plan.id}
                  className={`relative cursor-pointer transition-all hover:shadow-lg ${
                    selectedPlan === plan.id
                      ? 'ring-2 ring-[#0d9488] shadow-lg transform scale-[1.02]'
                      : 'hover:border-[#0d9488]/30'
                  } ${plan.id === 'professional' ? 'border-[#0d9488]/50' : ''}`}
                  onClick={() => handlePlanSelect(plan.id)}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.id === 'professional' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#0d9488]">Mais Popular</Badge>
                    </div>
                  )}
                  {plan.id === 'master' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-amber-500">Multi-Empresa</Badge>
                    </div>
                  )}
                  
                  <div className="p-6">
                    {/* Plan Icon */}
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanColor(plan.id)} flex items-center justify-center text-white mb-4`}>
                      {getPlanIcon(plan.id)}
                    </div>
                    
                    {/* Plan Name */}
                    <h3 className="text-xl font-bold text-[#1e3a5f] mb-4">{plan.name}</h3>
                    
                    {/* Employee Limit */}
                    <div className="flex items-center gap-2 mb-4 p-2 bg-[#e8f4fc] rounded-lg">
                      <Users className="h-4 w-4 text-[#0d9488]" />
                      <span className="text-sm font-medium text-[#1e3a5f]">
                        {plan.employees_label}
                      </span>
                    </div>
                    
                    {/* Features */}
                    <ul className="space-y-2 mb-4">
                      {plan.features.slice(0, 4).map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <Check className="h-4 w-4 text-[#0d9488] shrink-0 mt-0.5" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Selection Indicator */}
                    <div className={`w-full py-2 rounded-lg text-center font-medium ${
                      selectedPlan === plan.id
                        ? 'bg-[#0d9488] text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedPlan === plan.id ? 'Selecionado' : 'Selecionar'}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Continue Button */}
            <div className="text-center">
              <Button
                size="lg"
                onClick={() => setStep(2)}
                className="bg-[#0d9488] hover:bg-[#0f766e] px-12"
                data-testid="continue-to-registration"
              >
                Continuar com {plans.find(p => p.id === selectedPlan)?.name || 'Starter'}
              </Button>
              <p className="text-sm text-gray-500 mt-3">
                Já tem uma conta?{' '}
                <Link to="/login" className="text-[#0d9488] hover:underline font-medium">
                  Fazer login
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Registration Form */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto">
            {/* Selected Plan Summary */}
            <div className="bg-white rounded-xl p-4 mb-6 border border-[#0d9488]/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getPlanColor(selectedPlan)} flex items-center justify-center text-white`}>
                  {getPlanIcon(selectedPlan)}
                </div>
                <div>
                  <p className="font-semibold text-[#1e3a5f]">
                    Plano {plans.find(p => p.id === selectedPlan)?.name || 'Starter'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {plans.find(p => p.id === selectedPlan)?.employees_label || 'Até 50 funcionários'}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                Trocar plano
              </Button>
            </div>

            <Card className="p-8" data-testid="register-card">
              <div className="mb-6">
                <Badge className="bg-emerald-100 text-emerald-700 mb-2">7 dias grátis</Badge>
                <h2 className="text-2xl font-bold text-[#1e3a5f]">Complete seu cadastro</h2>
                <p className="text-gray-500">Preencha os dados para iniciar seu teste gratuito</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#0d9488] text-white text-xs flex items-center justify-center">1</div>
                    Dados Pessoais
                  </h3>
                  <div>
                    <Label htmlFor="nome">Nome Completo</Label>
                    <Input
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      data-testid="nome-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Senha</Label>
                    <PasswordInput
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#0d9488] text-white text-xs flex items-center justify-center">2</div>
                    Dados da Empresa
                  </h3>
                  <div>
                    <Label htmlFor="company.nome">Nome da Empresa</Label>
                    <Input
                      id="company.nome"
                      name="company.nome"
                      value={formData.company.nome}
                      onChange={handleChange}
                      required
                      data-testid="company-nome-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company.cnpj">CNPJ</Label>
                      <Input
                        id="company.cnpj"
                        name="company.cnpj"
                        value={formData.company.cnpj}
                        onChange={handleChange}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        required
                        className={cnpjError ? 'border-red-500' : ''}
                        data-testid="cnpj-input"
                      />
                      {cnpjError && (
                        <p className="text-sm text-red-500 mt-1">{cnpjError}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="company.telefone">Contato</Label>
                      <Input
                        id="company.telefone"
                        name="company.telefone"
                        value={formData.company.telefone}
                        onChange={handleChange}
                        placeholder="(00) 00000-0000"
                        required
                        data-testid="telefone-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company.numero_funcionarios">Número de Funcionários</Label>
                      <Input
                        id="company.numero_funcionarios"
                        name="company.numero_funcionarios"
                        type="number"
                        value={formData.company.numero_funcionarios}
                        onChange={handleChange}
                        required
                        data-testid="numero-funcionarios-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company.setor_atuacao">Setor de Atuação</Label>
                      <Input
                        id="company.setor_atuacao"
                        name="company.setor_atuacao"
                        value={formData.company.setor_atuacao}
                        onChange={handleChange}
                        required
                        data-testid="setor-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Terms Acceptance Checkbox */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border" data-testid="terms-checkbox-container">
                  <Checkbox
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked)}
                    className="mt-1"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms-checkbox" className="text-sm text-slate-600 cursor-pointer">
                    Li e aceito os{' '}
                    <Link 
                      to="/termos-de-uso" 
                      target="_blank" 
                      className="text-[#0d9488] hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Termos de Uso
                    </Link>{' '}
                    e a{' '}
                    <Link 
                      to="/politica-de-privacidade" 
                      target="_blank" 
                      className="text-[#0d9488] hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Política de Privacidade
                    </Link>{' '}
                    da plataforma CERP Brasil NR1.
                  </label>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    type="submit"
                    className="w-full bg-[#0d9488] hover:bg-[#0f766e] text-white py-6"
                    disabled={loading || !termsAccepted}
                    data-testid="register-submit-button"
                  >
                    {loading ? 'Cadastrando...' : 'Iniciar Teste Grátis de 7 Dias'}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="text-gray-500"
                  >
                    Voltar para seleção de plano
                  </Button>
                </div>

                <p className="text-xs text-center text-gray-500">
                  Ao se cadastrar, seus dados serão tratados conforme nossa Política de Privacidade
                </p>
              </form>
            </Card>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Já tem uma conta?{' '}
                <Link
                  to="/login"
                  className="text-[#0d9488] hover:text-[#0f766e] font-medium"
                  data-testid="login-link"
                >
                  Fazer login
                </Link>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
